import { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../context/AppContext';

const actionLabels = {
  approve: 'Approve',
  reject: 'Reject',
  enable: 'Enable',
  disable: 'Disable',
  force_logout: 'Force Logout',
  reset_password: 'Reset Password',
  delete: 'Delete'
};

const confirmationActions = ['disable', 'force_logout', 'reset_password', 'delete'];

const confirmationCopy = {
  disable: {
    title: 'Disable User',
    message: 'This user will no longer be able to access the platform until enabled again.',
    confirmLabel: 'Disable'
  },
  force_logout: {
    title: 'Force Logout',
    message: 'This will end the user session and require them to sign in again.',
    confirmLabel: 'Force Logout'
  },
  reset_password: {
    title: 'Reset Password',
    message: 'This will start the password reset flow for this user.',
    confirmLabel: 'Reset Password'
  },
  delete: {
    title: 'Delete User',
    message: 'This action can remove the user account and should only be used when you are sure.',
    confirmLabel: 'Delete'
  }
};

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

function unwrapPayload(response) {
  if (typeof response?.body === 'string') {
    return safeJsonParse(response.body) || response;
  }

  if (typeof response?.data?.body === 'string') {
    return safeJsonParse(response.data.body) || response.data;
  }

  return response?.data || response;
}

function unwrapList(response) {
  const payload = unwrapPayload(response);
  const candidates = [payload, payload?.users, payload?.data, response?.users, response?.data?.users];
  return candidates.find(Array.isArray) || [];
}

function firstValue(source, keys, fallback = '--') {
  const key = keys.find(
    (candidate) => source?.[candidate] !== undefined && source?.[candidate] !== null
  );
  return key ? source[key] : fallback;
}

function normalizeUser(user, index) {
  const email = firstValue(user, ['email', 'user_email', 'Email', 'UserEmail'], '');
  const status = String(firstValue(user, ['status', 'Status'], 'UNKNOWN')).toUpperCase();
  const enabled = firstValue(user, ['enabled', 'Enabled', 'is_enabled'], false);

  return {
    id: firstValue(user, ['username', 'id', 'user_id', 'sub'], email || index),
    username: firstValue(user, ['username', 'id', 'user_id', 'sub'], '--'),
    email,
    name: firstValue(user, ['name', 'full_name', 'user_name', 'Name'], 'N/A'),
    status,
    enabled: enabled === true || enabled === 'true' || enabled === 'TRUE',
    created: firstValue(user, ['created', 'created_at', 'registrationDate', 'registration_date'], '--'),
    raw: user
  };
}

function statusClass(value) {
  return String(value).toLowerCase().replace(/\s+/g, '-');
}

function getDetailEntries(details) {
  const payload = unwrapPayload(details);
  const objectValue = Array.isArray(payload) ? payload[0] : payload;

  if (!objectValue || typeof objectValue !== 'object') {
    return [];
  }

  return Object.entries(objectValue).map(([key, value]) => ({
    key,
    value: typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : String(value)
  }));
}

function UserTable({ title, description, users, loading, emptyText, renderActions }) {
  return (
    <div className="table-card">
      <div className="card-header">
        <div>
          <h4>{title}</h4>
          <p>{description}</p>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Username</th>
              <th>Status</th>
              <th>Enabled</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7">Loading users...</td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="7">{emptyText}</td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={`${title}-${user.id}`}>
                  <td>
                    <strong>{user.name}</strong>
                  </td>
                  <td>{user.email || '--'}</td>
                  <td>{user.username}</td>
                  <td>
                    <span className={`status-pill ${statusClass(user.status)}`}>{user.status}</span>
                  </td>
                  <td>{user.enabled ? 'Yes' : 'No'}</td>
                  <td>{user.created}</td>
                  <td>{renderActions(user)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UsersPage() {
  const { api } = useAppContext();
  const [allUsers, setAllUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  const activeUsers = useMemo(
    () => allUsers.filter((user) => user.status === 'CONFIRMED'),
    [allUsers]
  );

  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const usersResponse = await api.getAdminUsers();
      const normalizedUsers = unwrapList(usersResponse).map(normalizeUser);

      setAllUsers(normalizedUsers);
      setPendingUsers(normalizedUsers.filter((user) => user.status === 'UNCONFIRMED'));
    } catch (caughtError) {
      setError(caughtError.message || 'Unable to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [api]);

  const executeAction = async (reqType, userEmail) => {
    setActionLoading(`${reqType}:${userEmail}`);
    setError('');
    setNotice('');

    try {
      await api.updateAdminUser(reqType, userEmail);
      setNotice(`${actionLabels[reqType]} completed for ${userEmail}.`);
      await loadUsers();
    } catch (caughtError) {
      setError(caughtError.message || `${actionLabels[reqType]} failed`);
    } finally {
      setActionLoading('');
    }
  };

  const runAction = async (reqType, userEmail) => {
    if (!userEmail) {
      setError('User email is missing for this action.');
      return;
    }

    if (confirmationActions.includes(reqType)) {
      setPendingAction({ reqType, userEmail });
      return;
    }

    await executeAction(reqType, userEmail);
  };

  const confirmPendingAction = async () => {
    if (!pendingAction) {
      return;
    }

    const { reqType, userEmail } = pendingAction;
    setPendingAction(null);
    await executeAction(reqType, userEmail);
  };

  const showUserDetails = async (user) => {
    if (!user.email) {
      setError('User email is missing for details.');
      return;
    }

    setDetailsLoading(true);
    setError('');
    setSelectedDetails({ user, entries: [] });

    try {
      const response = await api.getAdminUserDetails(user.email);
      setSelectedDetails({ user, entries: getDetailEntries(response) });
    } catch (caughtError) {
      setSelectedDetails({ user, entries: getDetailEntries(user.raw) });
      setError(caughtError.message || 'Unable to load user details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const detailButton = (user) => (
    <button
      type="button"
      className="ghost-button compact"
      disabled={Boolean(actionLoading)}
      onClick={() => showUserDetails(user)}
    >
      Details
    </button>
  );

  return (
    <section className="panel-section">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Section 1</p>
          <h3>User Management</h3>
        </div>
      </div>

      {error ? (
        <div className="error-banner" role="alert">
          {error}
        </div>
      ) : null}
      {notice ? <div className="success-banner">{notice}</div> : null}

      <UserTable
        title="Active Users"
        description="Users whose Cognito status is CONFIRMED."
        users={activeUsers}
        loading={loading}
        emptyText="No active users found."
        renderActions={(user) => (
          <div className="action-group">
            <button
              type="button"
              className="ghost-button compact"
              disabled={Boolean(actionLoading)}
              onClick={() => runAction(user.enabled ? 'disable' : 'enable', user.email)}
            >
              {user.enabled ? 'Disable' : 'Enable'}
            </button>
            {detailButton(user)}
          </div>
        )}
      />

      <UserTable
        title="Pending Users"
        description="Users whose Cognito status is UNCONFIRMED."
        users={pendingUsers}
        loading={loading}
        emptyText="No pending users found."
        renderActions={(user) => (
          <div className="action-group">
            <button
              type="button"
              className="primary-button compact"
              disabled={Boolean(actionLoading)}
              onClick={() => runAction('approve', user.email)}
            >
              Approve
            </button>
            <button
              type="button"
              className="ghost-button compact"
              disabled={Boolean(actionLoading)}
              onClick={() => runAction('reject', user.email)}
            >
              Reject
            </button>
            {detailButton(user)}
          </div>
        )}
      />

      <UserTable
        title="Active User Actions"
        description="Operational actions for confirmed users."
        users={activeUsers}
        loading={loading}
        emptyText="No active users available for actions."
        renderActions={(user) => (
          <div className="action-group">
            <button
              type="button"
              className="ghost-button compact"
              disabled={Boolean(actionLoading)}
              onClick={() => runAction('force_logout', user.email)}
            >
              Force Logout
            </button>
            <button
              type="button"
              className="ghost-button compact"
              disabled={Boolean(actionLoading)}
              onClick={() => runAction('reset_password', user.email)}
            >
              Reset Password
            </button>
            <button
              type="button"
              className="danger-button compact"
              disabled={Boolean(actionLoading)}
              onClick={() => runAction('delete', user.email)}
            >
              Delete
            </button>
          </div>
        )}
      />

      {pendingAction ? (
        <div className="modal-backdrop" role="presentation">
          <div className="modal-card alert-modal" role="alertdialog" aria-modal="true" aria-labelledby="action-alert-title">
            <div className="alert-icon" aria-hidden="true">!</div>
            <div>
              <p className="eyebrow">Confirm Action</p>
              <h4 id="action-alert-title">{confirmationCopy[pendingAction.reqType].title}</h4>
              <p className="supporting-text dark-text">
                {confirmationCopy[pendingAction.reqType].message}
              </p>
              <p className="alert-user-email">{pendingAction.userEmail}</p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="ghost-button compact"
                onClick={() => setPendingAction(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={pendingAction.reqType === 'delete' ? 'danger-button compact' : 'primary-button compact'}
                onClick={confirmPendingAction}
              >
                {confirmationCopy[pendingAction.reqType].confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {selectedDetails ? (
        <div className="modal-backdrop" role="presentation">
          <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="user-details-title">
            <div className="card-header">
              <div>
                <p className="eyebrow">User Details</p>
                <h4 id="user-details-title">{selectedDetails.user.email}</h4>
              </div>
              <button
                type="button"
                className="ghost-button compact"
                onClick={() => setSelectedDetails(null)}
              >
                Close
              </button>
            </div>
            {detailsLoading ? (
              <p className="supporting-text dark-text">Loading details...</p>
            ) : selectedDetails.entries.length === 0 ? (
              <p className="supporting-text dark-text">No extra details found.</p>
            ) : (
              <dl className="details-grid">
                {selectedDetails.entries.map((entry) => (
                  <div key={entry.key}>
                    <dt>{entry.key}</dt>
                    <dd>{entry.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default UsersPage;
